import { Pipe, PipeTransform, inject } from '@angular/core';
import { TimezoneService } from '../services/timezone.service';

@Pipe({
  name: 'timezone',
  standalone: true,
  pure: false
})
export class TimezonePipe implements PipeTransform {
  private tzService = inject(TimezoneService);

  private formats: Record<string, Intl.DateTimeFormatOptions> = {
    short: { year: '2-digit', month: 'short', day: '2-digit' },
    long: { year: 'numeric', month: 'long', day: '2-digit', weekday: 'long' },
    time: { hour: '2-digit', minute: '2-digit', second: '2-digit' },
    datetime: { 
      year: 'numeric', month: 'short', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    },
    noFormat: { weekday: 'long', year: 'numeric', month: 'long', day: '2-digit' },
    startEndTime: { 
      year: 'numeric', month: 'short', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: true
    },
    minutes: { minute: '2-digit' }
  };

  transform(
    value: string | number | Date | null | undefined, 
    format: keyof typeof this.formats | Intl.DateTimeFormatOptions = 'datetime'
  ): string {
    if (value === null || value === undefined) return '';

    const tz = this.tzService.getTimezone();
    let date: Date;

    // --- Handle string ---
    if (typeof value === 'string') {
      // Only parse ISO datetime strings
      if (!/^\d{4}-\d{2}-\d{2}T\d/.test(value)) {
        return value;
      }

      // Add 'Z' for UTC unless using startEndTime
      if (typeof format === 'string' && format !== 'startEndTime' && !value.endsWith('Z')) {
        value += 'Z';
      }

      date = new Date(value);

    // --- Handle number ---
    } else if (typeof value === 'number') {
      // If very small number, treat as plain number
      if (value < 1e11) return value.toString();
      date = new Date(value);

    // --- Handle Date object ---
    } else if (value instanceof Date) {
      date = value;

    } else {
      return value as string;
    }

    // Invalid date fallback
    if (isNaN(date.getTime())) return value as string;

    // Select format
    const options = typeof format === 'string' ? this.formats[format] ?? this.formats['datetime'] : format;

    return new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      ...options
    }).format(date);
  }
}
